// Browser notifications + tab-title badge when a watched agent finishes
// working (busy → ready). The SSE session.state stream already drives
// the agentStates map, so this hook just diffs transitions and fires.
//
// Scope is deliberately narrow: only watched agents (the active session
// in App), and only while the tab is hidden — so you're notified exactly
// when you've tabbed away waiting on a reply, never spammed while you're
// looking at the chat.

import { useEffect, useRef } from 'react';
import type { AgentState } from '../api/types';

const ENABLED_KEY = 'cloud-notify-done';

export function notificationsEnabled(): boolean {
  return localStorage.getItem(ENABLED_KEY) === '1';
}
export function setNotificationsEnabled(on: boolean) {
  localStorage.setItem(ENABLED_KEY, on ? '1' : '0');
}

const BUSY = new Set(['thinking', 'tool']);
const IDLE = new Set(['ready', 'idle']);

// Base document title so we can restore it after clearing the badge.
const BASE_TITLE = typeof document !== 'undefined' ? document.title : 'AgentOS';

function setBadge(count: number) {
  if (typeof document === 'undefined') return;
  document.title = count > 0 ? `(${count}) ${BASE_TITLE}` : BASE_TITLE;
}

export function useDoneNotifications(
  agentStates: Record<string, AgentState>,
  watched: string[],
) {
  // Previous state per watched agent — to detect the busy→idle edge.
  const prevRef = useRef<Record<string, string>>({});
  // Count of unseen "done" events while the tab was hidden. Cleared when
  // the tab regains focus.
  const badgeRef = useRef(0);

  // Clear the badge when the user comes back to the tab.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) {
        badgeRef.current = 0;
        setBadge(0);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const watchedSet = new Set(watched);
    for (const name of watched) {
      const cur = agentStates[name]?.state;
      if (!cur) continue;
      const prev = prevRef.current[name];
      // Busy → idle edge = the agent just finished a turn.
      if (prev && BUSY.has(prev) && IDLE.has(cur)) {
        // Only surface while the tab is hidden — no point pinging the
        // user about an agent they're actively watching.
        if (document.hidden && notificationsEnabled()) {
          badgeRef.current += 1;
          setBadge(badgeRef.current);
          fireNotification(name);
        }
      }
      prevRef.current[name] = cur;
    }
    // Drop prev-state for agents no longer watched so a later re-add
    // doesn't fire a stale transition.
    for (const k of Object.keys(prevRef.current)) {
      if (!watchedSet.has(k)) delete prevRef.current[k];
    }
  }, [agentStates, watched]);
}

function fireNotification(agentName: string) {
  try {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    const n = new Notification(`${agentName} ist fertig`, {
      body: 'Der Agent hat geantwortet — tippen zum Öffnen.',
      icon: '/pwa-192.png',
      badge: '/pwa-192.png',
      tag: `agent-done-${agentName}`, // collapse repeats for the same agent
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* no-op */ }
      n.close();
    };
  } catch { /* notifications unsupported / blocked */ }
}

// Request OS permission — must be called from a user gesture. Returns the
// resulting permission so callers can reflect it in the UI.
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  try {
    return await Notification.requestPermission();
  } catch {
    return 'denied';
  }
}
